import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Handshake, MapPin, Mail } from "lucide-react";
import V2Layout from "@/pages/v2/V2Layout";
import Seo from "@/components/Seo";

const fade = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export default function V2Home() {
  return (
    <V2Layout>
      <div data-testid="v2-home">
        <Seo
          title="WHO — Warm Hand Off · Strengthening the Connections That Support Veterans"
          path="/v2"
          description="WHO is developing an innovative approach to connecting veterans and their families with the resources they need — while strengthening collaboration among the organizations committed to serving them."
        />

        {/* Section 1 — Hero */}
        <section className="relative bg-navy-deep" data-testid="v2-hero">
          <div className="relative w-full aspect-[16/10] md:aspect-[16/9] lg:aspect-[16/8] max-h-[720px] overflow-hidden">
            <img
              src="/assets/pittsburgh-hero-v2.jpg"
              alt="Five figures — four veterans and civilians and a child holding hands — overlooking the Pittsburgh skyline at sunset"
              className="absolute inset-0 w-full h-full object-cover brightness-110"
              data-testid="v2-hero-image"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/55 via-transparent to-navy-deep/30 pointer-events-none md:hidden" aria-hidden />
            {/* Desktop: copy sits in a bottom gradient strip, below the figures' sightline */}
            <div className="absolute inset-x-0 bottom-0 hidden md:block">
              <div className="bg-gradient-to-t from-navy-deep/90 via-navy-deep/50 to-transparent pt-20 pb-8">
                <div className="max-w-[1100px] mx-auto w-full px-5 lg:px-8">
                  <HeroText />
                </div>
              </div>
            </div>
          </div>
          <div className="md:hidden px-5 py-8 bg-navy-deep">
            <HeroText />
          </div>
        </section>

        {/* Section 2 — Why WHO Exists */}
        <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="v2-why-section">
          <div className="max-w-[760px] mx-auto px-5 lg:px-8 text-center">
            <motion.div {...fade}>
              <p className="eyebrow text-gold-deep">Why WHO exists</p>
              <h2 className="mt-3 font-display font-black text-navy tracking-tight text-3xl sm:text-4xl leading-tight">
                A referral is not a result. A connection is.
              </h2>
              <p className="mt-5 text-slateSoft leading-relaxed" data-testid="v2-why-copy">
                Dedicated organizations serve veterans every day. WHO is being developed to strengthen
                connections across those organizations, helping make coordination and follow-through more
                effective. The challenge is fragmentation across systems, not a lack of commitment from
                the people working within them — WHO complements their work, never replaces it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section 3 — How WHO Works */}
        <section className="py-16 lg:py-20 bg-cream-panel border-b border-borderSubtle" data-testid="v2-how-section">
          <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
            <motion.div {...fade} className="text-center max-w-2xl mx-auto">
              <p className="eyebrow text-gold-deep">How WHO works</p>
              <h2 className="mt-3 font-display font-black text-navy tracking-tight text-3xl sm:text-4xl leading-tight">
                Two simple pathways.
              </h2>
              <p className="mt-4 text-slateSoft leading-relaxed">
                Discover resources today. Learn about our vision for more coordinated, accountable
                connections tomorrow.
              </p>
            </motion.div>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              <motion.div {...fade} className="p-7 bg-white border border-borderSubtle rounded-lg hover:border-gold transition-colors" data-testid="v2-path-discovery">
                <Compass className="w-7 h-7 text-gold-deep" />
                <h3 className="mt-4 font-display font-bold text-navy text-xl">Discover Resources</h3>
                <p className="mt-2 text-sm text-slateSoft leading-relaxed">
                  Explore information about services, programs, and opportunities — available today, no
                  account needed.
                </p>
                <span className="mt-3 inline-flex text-[10px] uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">Available now</span>
                <div className="mt-4">
                  <Link to="/v2/resources" className="inline-flex items-center gap-1.5 text-navy font-semibold text-sm underline decoration-gold underline-offset-4 hover:text-gold-deep transition-colors" data-testid="v2-path-discovery-link">
                    Explore the directory <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
              <motion.div {...fade} className="p-7 bg-navy-deep text-cream rounded-lg" data-testid="v2-path-coordinated">
                <Handshake className="w-7 h-7 text-gold" />
                <h3 className="mt-4 font-display font-bold text-cream text-xl">Coordinated Services</h3>
                <p className="mt-2 text-sm text-cream/75 leading-relaxed">
                  Learn about WHO&rsquo;s planned approach: permission-based connections and follow-through
                  supported by WHO Guides&trade;.
                </p>
                <span className="mt-3 inline-flex text-[10px] uppercase tracking-widest font-bold text-cream/70 bg-white/5 border border-white/15 rounded-full px-2.5 py-1">Planned capability</span>
                <div className="mt-4">
                  <Link to="/v2/approach" className="inline-flex items-center gap-1.5 text-gold-light font-semibold text-sm underline decoration-gold underline-offset-4 hover:text-gold transition-colors" data-testid="v2-path-coordinated-link">
                    Our approach <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Section 4 — Pittsburgh Roots, National Vision */}
        <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="v2-pittsburgh-section">
          <div className="max-w-[760px] mx-auto px-5 lg:px-8 text-center">
            <motion.div {...fade}>
              <MapPin className="w-7 h-7 text-gold-deep mx-auto" />
              <h2 className="mt-4 font-display font-black text-navy tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
                Pittsburgh is our foundation. Veterans are our purpose. National impact is our ambition.
              </h2>
              <p className="mt-5 text-slateSoft leading-relaxed">
                WHO began in Pittsburgh — a city with a long tradition of building, reinvention, and service.
                We are starting here, learning what works, and developing a model intended to be useful far
                beyond one community.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section 5 — Connect With WHO */}
        <section className="py-16 lg:py-20 bg-navy-deep text-cream" data-testid="v2-connect-section">
          <div className="max-w-[760px] mx-auto px-5 lg:px-8 text-center">
            <motion.div {...fade}>
              <Mail className="w-7 h-7 text-gold mx-auto" />
              <h2 className="mt-4 font-display font-black text-cream tracking-tight text-3xl sm:text-4xl leading-tight">
                Connect With WHO.
              </h2>
              <p className="mt-5 text-cream/80 leading-relaxed">
                We welcome veteran-serving organizations, community leaders, researchers, technology
                innovators, prospective funders, and institutional partners.
              </p>
              <Link
                to="/v2/contact"
                data-testid="v2-connect-cta"
                className="mt-7 inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-7 py-3.5 rounded-md transition-colors"
              >
                Contact WHO <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </V2Layout>
  );
}

function HeroText() {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
      <h1 className="font-display font-black text-cream tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-[2.6rem]" data-testid="v2-hero-headline">
        Strengthening the Connections That Support Veterans.
      </h1>
      <p className="mt-4 text-cream/90 text-base lg:text-lg leading-relaxed" data-testid="v2-hero-subcopy">
        We&rsquo;re building a better way for veterans and their families to find resources, navigate
        opportunities, and connect with organizations committed to serving them.
      </p>
      <p className="mt-3 text-gold-light font-display font-bold text-lg" data-testid="v2-hero-tagline">If Not You, Then WHO.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/v2/about" data-testid="v2-hero-cta-primary" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3 rounded-md transition-colors">
          Discover WHO <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
        <Link to="/v2/resources" data-testid="v2-hero-cta-secondary" className="inline-flex items-center gap-2 border border-cream/40 hover:border-gold hover:text-gold md:text-cream text-cream font-semibold px-6 py-3 rounded-md transition-colors">
          Explore Resources
        </Link>
      </div>
    </motion.div>
  );
}
